import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import Seo, { hotelSchema, organizationSchema } from "@/components/Seo";
import {
  IntroSection,
  PropertiesSection,
  DiningSection,
  ServicesSection,
  GrowSection,
  TestimonialSection,
  LocationRibbon,
} from "@/components/home/HomeSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [organizationSchema, hotelSchema],
};

const ROOM_SHOWCASE = {
  "premium-room": {
    desc: "Spacious king bed, dedicated work desk, high-speed Wi-Fi, black-out curtains, and 24/7 in-room dining.",
    cta: "Book Premium Room",
  },
  "premium-twin-room": {
    desc: "Luxury bedding, city/garden views, electronic safe, minibar, tea/coffee maker, and premium bath amenities.",
    cta: "Book Premium Twin Room",
  },
};

const RoomsSection = ({ rooms }) => {
  if (rooms.length < 2) return null;
  return (
    <section data-testid="section-rooms" className="py-24 md:py-32 bg-paper-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between gap-8 mb-12 flex-wrap">
          <div>
            <div className="overline">Rooms</div>
            <h2 className="mt-5 font-serif text-4xl md:text-5xl text-ink-900">
              Two ways to stay.
            </h2>
          </div>
          <Link
            to="/rooms"
            data-testid="rooms-see-all"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-700 hover:text-sage-dark"
          >
            All rooms <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {rooms.slice(0, 2).map((r) => {
            const showcase = ROOM_SHOWCASE[r.slug];
            return (
              <div key={r.slug} data-testid={`home-room-${r.slug}`} className="group block bg-paper-50">
                <Link to={`/rooms/${r.slug}`} className="block relative aspect-[4/3] overflow-hidden bg-paper-200">
                  <img
                    src={r.image}
                    alt={`${r.name} — ${r.tagline} at Hotel Azulo Inn`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                </Link>
                <div className="p-6 md:p-8">
                  <div className="overline">{r.bed}</div>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl text-ink-900">{r.name}</h3>
                  <p className="mt-3 text-ink-600 leading-relaxed">
                    {showcase ? showcase.desc : r.tagline}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <Link
                      to={`/book?room=${r.slug}`}
                      data-testid={`home-room-book-${r.slug}`}
                      className="inline-flex items-center h-11 px-6 bg-sage text-paper-50 text-[11px] font-semibold tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors"
                    >
                      {showcase ? showcase.cta : "Book Now"}
                    </Link>
                    <Link
                      to={`/rooms/${r.slug}`}
                      data-testid={`home-room-view-${r.slug}`}
                      className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-sage-dark hover:text-ink-900"
                    >
                      View Room <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-10 text-sm text-ink-600 italic">
          Tariffs are dynamic — please ask reservations for your preferred dates.
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    axios.get(`${API}/rooms`).then((r) => setRooms(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Boutique Comfort in Safdarjung Enclave, New Delhi"
        description="Hotel Azulo Inn — boutique comfort in Safdarjung Enclave, New Delhi. 10 minutes from Chanakyapuri Diplomatic Enclave, US Embassy, and AIIMS. Book direct and save 15% vs OTAs."
        path="/"
        schema={HOME_SCHEMA}
      />
      <Hero />
      <LocationRibbon />
      <IntroSection />
      <PropertiesSection />
      <DiningSection />
      <RoomsSection rooms={rooms} />
      <ServicesSection />
      <GrowSection />
      <TestimonialSection />
    </div>
  );
}
